import {
  OktonEmptyState,
  OktonSearchFilter,
  OktonSelect,
} from '@okton/platform-ui';
import React, { useState, useMemo, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Flex,
  Text,
  SimpleGrid,
  Button,
  Badge,
  Heading,
  useColorModeValue,
  HStack,
  Icon,
  Switch,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useQueryClient } from 'react-query';
import type { AxiosError } from 'axios';
import {
  Eye,
  Upload,
  RefreshCw,
  Package,
  CheckCircle,
  AlertTriangle,
  Pencil,
} from 'lucide-react';
import { OktonReactTable as Table } from '@okton/platform-ui/react-table';

import backend from '../../services/backend';
import Page from '../../layout/IntegraAppPage';
import ProdutoModal from '../../components/Modals/ProdutosModal/ProdutoModal';
import { useProdutos } from '../../services/hooks/useProdutos';
import formatValue from '../../utils/formatValue';

// ==============================
// Tipagens
// ==============================

type Status = 'ativo' | 'inativo';
type CategoriaFiltro = 'todas' | string;
type ApiError = AxiosError<{ message?: string }>;
type ProdutoRow = { row: { original: Produto } };

export type Produto = {
  id: number;
  nome: string;
  sku: string;
  marca: string;
  categoria: string;
  categoria_nome?: string;
  categoria_id?: number | string;
  preco: string;
  promo?: string;
  estoque: number;
  status: boolean;
  vendas: number;
  canais: string[];
};

const Produtos = () => {
  // ==============================
  // Hooks base
  // ==============================

  const queryClient = useQueryClient();
  const history = useHistory();
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ==============================
  // Estados
  // ==============================

  const [produtoSelecionado, setProdutoSelecionado] = useState<Produto | null>(
    null,
  );

  const [estaImportando, setEstaImportando] = useState(false);
  const [status, setStatus] = useState<'todos' | Status>('todos');
  const [categoria, setCategoria] = useState<CategoriaFiltro>('todas');
  const [busca, setBusca] = useState('');
  const [estaEnviandoTodos, setEstaEnviandoTodos] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();

  // ==============================
  // Cores
  // ==============================

  const corDeFundo = useColorModeValue('white', 'gray.800');
  const corDaBorda = useColorModeValue('gray.200', 'gray.700');
  const corDoTexto = useColorModeValue('gray.800', 'gray.100');
  const corDoTextoSecundario = useColorModeValue('gray.500', 'gray.400');

  // ==============================
  // Busca de produtos
  // ==============================

  const {
    data: produtos = [],
    refetch: carregarProdutos,
    isFetching,
  } = useProdutos({
    busca,
    status,
    categoria,
  });

  const categoriasDosProdutos = useMemo(() => {
    const nomes = produtos
      .map((produto: Produto) => produto.categoria_nome ?? produto.categoria)
      .filter((nome): nome is string => Boolean(nome));

    return Array.from(new Set(nomes)).sort();
  }, [produtos]);

  // ==============================
  // Importação de planilha
  // ==============================

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('planilha', file);

    setEstaImportando(true);

    try {
      const response = await backend.post(
        '/produtos/importar-excel',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );

      await queryClient.invalidateQueries(['produtos']);

      history.push({
        pathname: '/produtos/revisar-importacao',
        state: {
          produtosImportados: response.data,
        },
      });

      toast({
        title: 'Planilha importada!',
        description: 'Revise os dados antes de salvar.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      const err = error as ApiError;
      toast({
        title: 'Erro ao importar',
        description:
          err.response?.data?.message || 'Falha ao processar arquivo.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setEstaImportando(false);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // ==============================
  // Modal de produto
  // ==============================

  const abrirModalProduto = useCallback(
    async (id: number) => {
      try {
        const response = await backend.get(`/produtos/${id}`);

        setProdutoSelecionado(response.data);
        onOpen();
      } catch (error) {
        const err = error as ApiError;
        toast({
          title: 'Erro ao carregar produto',
          description: err.response?.data?.message || 'Tente novamente.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    },
    [onOpen, toast],
  );

  // ==============================
  // Status do produto
  // ==============================

  const alterarStatusProduto = useCallback(
    async (id: number, ativo: boolean) => {
      try {
        await backend.put(`/produtos/${id}`, {
          status: ativo ? 'ativo' : 'inativo',
        });

        await queryClient.invalidateQueries(['produtos']);

        toast({
          title: 'Status alterado com sucesso!',
          status: 'success',
          duration: 2500,
          isClosable: true,
        });
      } catch (error) {
        const err = error as ApiError;
        toast({
          title: 'Erro ao alterar status',
          description: err.response?.data?.message || 'Tente novamente.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    },
    [queryClient, toast],
  );

  // ==============================
  // Sincronização
  // ==============================

  const sincronizarProduto = useCallback(
    async (produto: Produto) => {
      try {
        await backend.post('/sincroniza-produtos', {
          produto_id: produto.id,
          plataforma: 'mercadolivre',
        });

        toast({
          title: 'Produto enviado!',
          description: 'Sincronizado com Mercado Livre.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } catch (error) {
        const err = error as ApiError;
        toast({
          title: 'Erro ao sincronizar',
          description:
            err.response?.data?.message || 'Falha ao enviar produto.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    },
    [toast],
  );

  const sincronizarTodosProdutos = async () => {
    const novaGuia = window.open('', '_blank');

    setEstaEnviandoTodos(true);

    try {
      const response = await backend.post('/sincronizar-todos', {
        plataforma: 'mercadolivre',
        enviar_todos: true,
      });

      const batchId = response.data.batch_id;

      localStorage.setItem(
        `sincronizacao_lote_${batchId}`,
        JSON.stringify(response.data),
      );

      if (novaGuia) {
        novaGuia.location.href = `/produtos/sincronizacao-em-lote/${batchId}`;
      }

      toast({
        title: 'Produtos enviados!',
        description: 'Sincronização de todos os produtos iniciada.',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      const err = error as ApiError;
      if (novaGuia) {
        novaGuia.close();
      }

      toast({
        title: 'Erro ao enviar todos',
        description:
          err.response?.data?.message || 'Falha ao sincronizar os produtos.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setEstaEnviandoTodos(false);
    }
  };

  // ==============================
  // Filtros locais
  // ==============================

  const produtosFiltrados = useMemo(() => {
    return produtos.filter((item: Produto) => {
      const termo = busca.toLowerCase();

      const bateBusca =
        item.nome.toLowerCase().includes(termo) ||
        item.sku.toLowerCase().includes(termo) ||
        item.id.toString().includes(termo);

      const ativo = Boolean(Number(item.status));

      const bateStatus =
        status === 'todos' || (status === 'ativo' ? ativo : !ativo);

      const categoriaProduto = String(
        item.categoria_nome ?? item.categoria ?? '',
      ).trim();

      const bateCategoria =
        categoria === 'todas' || categoriaProduto === categoria;

      return bateBusca && bateStatus && bateCategoria;
    });
  }, [produtos, busca, status, categoria]);

  // ==============================
  // Colunas da tabela
  // ==============================

  const colunasDaTabela = useMemo(
    () => [
      {
        Header: 'PRODUTO',
        accessor: 'nome',
        Cell: ({ row }: ProdutoRow) => (
          <Box>
            <HStack spacing={2}>
              <Icon as={Package} color="blue.400" />
              <Text fontWeight="600">{row.original.nome}</Text>
            </HStack>

            <Text fontSize="xs" color="gray.500" ml={6}>
              {row.original.sku} • {row.original.marca}
            </Text>
          </Box>
        ),
      },
      {
        Header: 'CATEGORIA',
        accessor: 'categoria',
        Cell: ({ value }: { value: string }) => (
          <Badge variant="subtle">{value}</Badge>
        ),
      },
      {
        Header: 'PREÇO',
        accessor: 'preco',
        Cell: ({ value }: { value: string }) => (
          <Text fontWeight="600">{formatValue(value)}</Text>
        ),
      },
      {
        Header: 'ESTOQUE',
        accessor: 'estoque',
        Cell: ({ value }: { value: number }) => (
          <Badge colorScheme={value > 0 ? 'green' : 'red'}>{value} un</Badge>
        ),
      },
      {
        Header: 'STATUS',
        accessor: 'status',
        Cell: ({ row }: ProdutoRow) => {
          const [ativoLocal, setAtivoLocal] = useState(
            Boolean(Number(row.original.status)),
          );

          const toggle = async () => {
            const novo = !ativoLocal;

            setAtivoLocal(novo);

            try {
              await alterarStatusProduto(row.original.id, novo);
            } catch {
              setAtivoLocal(!novo);
            }
          };

          return (
            <HStack spacing={2}>
              <Switch
                isChecked={ativoLocal}
                onChange={toggle}
                colorScheme="green"
              />

              <Text fontSize="xs" color={ativoLocal ? 'green.500' : 'red.500'}>
                {ativoLocal ? 'Ativo' : 'Inativo'}
              </Text>
            </HStack>
          );
        },
      },
      {
        Header: 'AÇÕES',
        id: 'acoes',
        Cell: ({ row }: ProdutoRow) => (
          <HStack spacing={2}>
            <Button
              size="xs"
              variant="ghost"
              onClick={() => abrirModalProduto(row.original.id)}
            >
              <Eye size={16} />
            </Button>

            <Button
              size="xs"
              variant="ghost"
              onClick={() =>
                history.push(`/produtos/editar/${row.original.id}`)
              }
            >
              <Pencil size={16} />
            </Button>

            <Button
              size="xs"
              colorScheme="blue"
              onClick={() => sincronizarProduto(row.original)}
            >
              <Upload size={14} />
            </Button>
          </HStack>
        ),
      },
    ],
    [history, alterarStatusProduto, abrirModalProduto, sincronizarProduto],
  );

  // ==============================
  // Cards de resumo
  // ==============================

  const cartoes = [
    {
      label: 'Total',
      valor: produtos.length,
      icone: Package,
      cor: 'blue.500',
    },
    {
      label: 'Ativos',
      valor: produtos.filter((p: Produto) => Boolean(Number(p.status))).length,
      icone: CheckCircle,
      cor: 'green.500',
    },
    {
      label: 'Sem Estoque',
      valor: produtos.filter((p: Produto) => p.estoque === 0).length,
      icone: AlertTriangle,
      cor: 'red.500',
    },
  ];

  // ==============================
  // Render
  // ==============================

  return (
    <Page>
      <Box mb={6}>
        <Flex justify="space-between" wrap="wrap" gap={4}>
          <Box>
            <Heading size="lg" textAlign="left">
              Produtos
            </Heading>

            <Text fontSize="sm" color={corDoTextoSecundario}>
              Gestão de estoque e canais de venda
            </Text>
          </Box>

          <HStack spacing={3}>
            <input
              ref={fileInputRef}
              type="file"
              style={{ display: 'none' }}
              accept=".xlsx,.xls,.csv,.ods"
              onChange={handleFileChange}
            />

            <Button
              variant="outline"
              colorScheme="blue"
              leftIcon={<Upload size={16} />}
              isLoading={estaImportando}
              onClick={() => fileInputRef.current?.click()}
            >
              Importar produtos
            </Button>

            <Button
              colorScheme="blue"
              leftIcon={<Package size={16} />}
              onClick={() => history.push('/produtos/novo')}
            >
              Criar produto
            </Button>
          </HStack>
        </Flex>
      </Box>

      <SimpleGrid columns={[1, 2, 3]} spacing={5}>
        {cartoes.map(card => (
          <Box
            key={card.label}
            bg={corDeFundo}
            p={5}
            borderRadius="xl"
            border="1px solid"
            borderColor={corDaBorda}
          >
            <HStack justify="space-between" mb={3}>
              <Text fontSize="sm" color={corDoTextoSecundario}>
                {card.label}
              </Text>

              <Icon as={card.icone} color={card.cor} />
            </HStack>

            <Heading size="md" color={corDoTexto}>
              {card.valor}
            </Heading>
          </Box>
        ))}
      </SimpleGrid>

      <Box
        mt={7}
        bg={corDeFundo}
        border="1px solid"
        borderColor={corDaBorda}
        borderRadius="2xl"
      >
        <Box p={6} borderBottom="1px solid" borderColor={corDaBorda}>
          <Flex gap={3} wrap="wrap">
            <OktonSearchFilter
              value={busca}
              onChange={setBusca}
              placeholder="Buscar..."
            />

            <OktonSelect
              name="categoria"
              value={categoria}
              onChange={event => setCategoria(event.target.value)}
              w="180px"
            >
              <option value="todas">Todas categorias</option>
              {categoriasDosProdutos.map(nomeCategoria => (
                <option key={nomeCategoria} value={nomeCategoria}>
                  {nomeCategoria}
                </option>
              ))}
            </OktonSelect>

            <OktonSelect
              name="status"
              value={status}
              onChange={event =>
                setStatus(event.target.value as 'todos' | Status)
              }
              options={[
                { value: 'todos', label: 'Status' },
                { value: 'ativo', label: 'Ativo' },
                { value: 'inativo', label: 'Inativo' },
              ]}
              w="180px"
            />

            <HStack spacing={3}>
              <Button
                size="sm"
                bg="blue.500"
                padding="12px"
                color="white"
                leftIcon={<RefreshCw size={14} />}
                isLoading={isFetching}
                onClick={() => carregarProdutos()}
              >
                Atualizar
              </Button>

              <Button
                size="sm"
                bg="blue.500"
                color="white"
                leftIcon={<Upload size={14} />}
                isLoading={estaEnviandoTodos}
                onClick={sincronizarTodosProdutos}
              >
                Enviar todos
              </Button>
            </HStack>
          </Flex>
        </Box>

        <Box p={4}>
          <Table columns={colunasDaTabela} data={produtosFiltrados} />

          {produtosFiltrados.length === 0 && (
            <OktonEmptyState
              title="Nenhum produto encontrado"
              description="Não há produtos para os filtros atuais."
            />
          )}
        </Box>
      </Box>

      <ProdutoModal
        isOpen={isOpen}
        onClose={() => {
          setProdutoSelecionado(null);
          onClose();
        }}
        produto={produtoSelecionado}
        onAtualizarStatus={alterarStatusProduto}
        onSincronizar={() =>
          produtoSelecionado && sincronizarProduto(produtoSelecionado)
        }
      />
    </Page>
  );
};

export default Produtos;
